import { ColumnDef } from '@tanstack/react-table';
import { PencilIcon } from '@heroicons/react/24/outline';

export interface FormData {
  id: string;
  name: string;
  description: string;
  fieldsCount: number;
  aiCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  status: 'active' | 'draft' | 'archived';
}

interface ColumnOptions {
  onEdit?: (formId: string) => void;
}

function createColumns(options: ColumnOptions = {}): ColumnDef<FormData>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Nombre',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{row.getValue('name')}</span>
          {row.original.description && (
            <span className="text-sm text-gray-500 truncate max-w-xs">
              {row.original.description}
            </span>
          )}
        </div>
      ),
    },
  {
    accessorKey: 'fieldsCount',
    header: 'Campos',
    cell: ({ row }) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {row.getValue('fieldsCount')} campos
      </span>
    ),
  },
  {
    accessorKey: 'aiCompleted',
    header: 'IA Habilitada',
    cell: ({ row }) => {
      const aiCompleted = row.getValue('aiCompleted') as boolean;
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          aiCompleted 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {aiCompleted ? 'Sí' : 'No'}
        </span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'Estado',
    cell: ({ row }) => {
      const status = row.getValue('status') as string;
      const statusConfig = {
        active: { label: 'Activo', color: 'bg-green-100 text-green-800' },
        draft: { label: 'Borrador', color: 'bg-yellow-100 text-yellow-800' },
        archived: { label: 'Archivado', color: 'bg-gray-100 text-gray-800' },
      };
      
      const config = statusConfig[status as keyof typeof statusConfig] || {
        label: status || 'Desconocido',
        color: 'bg-gray-100 text-gray-800'
      };
      
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
          {config.label}
        </span>
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: 'Creado',
    cell: ({ row }) => {
      const date = new Date(row.getValue('createdAt'));
      return (
        <div className="text-sm text-gray-500">
          {date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </div>
      );
    },
  },
  {
    id: 'actions',
    header: 'Acciones',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <button
          className="btn btn-ghost btn-sm text-blue-600 hover:text-blue-800"
          onClick={() => {
            if (options.onEdit) {
              options.onEdit(row.original.id);
            }
          }}
          title="Editar formulario"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
      </div>
    ),
  },
];
}

// Export the function
export { createColumns };

// Export default columns for backward compatibility
export const columns = createColumns();
